<template>
  <div tabindex="-1" ref="image-viewer__wrapper" class="image-viewer__wrapper">
    <!-- CLOSE -->
    <button class="close-btn" @click="onClose">
      <SvgIcon iconClass="wiki-close" />
    </button>
    <!-- CANVAS -->
    <swiper ref="mySwiper" :auto-destroy="true" :auto-update="true" :options="swiperOptions" class="swiper-box">
      <swiper-slide v-for="(url, index) in urlList" :key="index" class="image-viewer__item no-swiping">
        <div class="image-viewer__canvas">
          <img
            ref="img"
            class="image-viewer__img"
            :key="url"
            :src="url"
            :style="imgStyle"
            @load="handleImgLoad"
            @error="handleImgError"
            @mousedown="handleMouseDown"
          />
        </div>
      </swiper-slide>
    </swiper>
    <div class="swiper-button-prev image-viewer__prev"></div>
    <div class="swiper-button-next image-viewer__next"></div>

    <!-- ACTIONS -->
    <div class="image-viewer__btn image-viewer__actions">
      <div class="image-viewer__actions__inner">
        <div class="swiper-pagination image-viewer__pagination"></div>
        <i class="image-viewer__icon image-viewer__actions__divider"></i>
        <i class="image-viewer__icon el-icon-zoom-out" @click="handleActions('zoomOut')"></i>
        <i class="image-viewer__icon el-icon-zoom-in" @click="handleActions('zoomIn')"></i>
        <i :class="['image-viewer__icon', mode.icon]" @click="toggleMode"></i>
        <i class="image-viewer__icon el-icon-refresh-left" @click="handleActions('anticlocelise')"></i>
        <i class="image-viewer__icon el-icon-refresh-right" @click="handleActions('clocelise')"></i>
      </div>
    </div>
  </div>
</template>

<script>
import { throttle } from "@/plugins/util";
import { Swiper, SwiperSlide } from "vue-awesome-swiper";

const Mode = {
  CONTAIN: {
    name: "contain",
    icon: "el-icon-full-screen"
  },
  ORIGINAL: {
    name: "original",
    icon: "el-icon-c-scale-to-original"
  }
};

const mousewheelEventName = "mousewheel";

export default {
  name: "ImageViewer",

  components: {
    Swiper,
    SwiperSlide
  },

  props: {
    urlList: {
      type: Array,
      default: () => []
    },
    index: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      loading: true,
      mode: Mode.CONTAIN,
      transform: {
        scale: 1,
        deg: 0,
        offsetX: 0,
        offsetY: 0,
        enableTransition: false
      },
      swiperOptions: {
        initialSlide: this.index,
        noSwipingClass: "no-swiping",
        navigation: {
          nextEl: ".image-viewer__next",
          prevEl: ".image-viewer__prev"
        },
        pagination: {
          el: ".image-viewer__pagination",
          type: "fraction"
        },
        on: {
          slideChange: () => {
            this.reset();
          }
        }
      }
    };
  },
  computed: {
    swiper() {
      return this.$refs.mySwiper.$swiper;
    },
    imgStyle() {
      const { scale, deg, offsetX, offsetY, enableTransition } = this.transform;
      const style = {
        transform: `scale(${scale}) rotate(${deg}deg)`,
        transition: enableTransition ? "transform .3s" : "",
        "margin-left": `${offsetX}px`,
        "margin-top": `${offsetY}px`
      };
      if (this.mode === Mode.CONTAIN) {
        style.maxWidth = style.maxHeight = "100%";
      }
      return style;
    }
  },
  mounted() {
    this.deviceSupportInstall();

    // add tabindex then wrapper can be focusable via Javascript
    // focus wrapper so arrow key can't cause inner scroll behavior underneath
    this.$nextTick(() => {
      if (this.$refs["image-viewer__wrapper"]) {
        this.$refs["image-viewer__wrapper"].focus();
      }
    });
  },
  destroyed() {},
  methods: {
    onClose() {
      this.deviceSupportUninstall();
      this.$imgViewClose();
    },
    deviceSupportInstall() {
      this._keyDownHandler = e => {
        e.stopPropagation();
        const keyCode = e.keyCode;
        switch (keyCode) {
          // ESC
          case 27:
            this.onClose();
            break;
          // SPACE
          case 32:
            this.toggleMode();
            break;
          // LEFT_ARROW
          case 37:
            this.swiper && this.swiper.slidePrev();
            break;
          // UP_ARROW
          case 38:
            this.handleActions("zoomIn");
            break;
          // RIGHT_ARROW
          case 39:
            this.swiper && this.swiper.slideNext();
            break;
          // DOWN_ARROW
          case 40:
            this.handleActions("zoomOut");
            break;
        }
      };
      this._mouseWheelHandler = throttle(e => {
        const delta = e.wheelDelta ? e.wheelDelta : -e.detail;
        if (delta > 0) {
          this.handleActions("zoomIn", {
            zoomRate: 0.05,
            enableTransition: false
          });
        } else {
          this.handleActions("zoomOut", {
            zoomRate: 0.05,
            enableTransition: false
          });
        }
      });

      document.addEventListener("keydown", this._keyDownHandler);
      document.addEventListener(mousewheelEventName, this._mouseWheelHandler);
    },
    deviceSupportUninstall() {
      document.removeEventListener("keydown", this._keyDownHandler);
      document.removeEventListener(mousewheelEventName, this._mouseWheelHandler);
      this._keyDownHandler = null;
      this._mouseWheelHandler = null;
    },
    handleImgLoad(e) {
      this.loading = false;
    },
    handleImgError(e) {
      this.loading = false;
      e.target.alt = "加载失败";
    },
    handleMouseDown(e) {
      if (this.loading || e.button !== 0) return;

      const { offsetX, offsetY } = this.transform;
      const startX = e.pageX;
      const startY = e.pageY;
      this._dragHandler = ev => {
        this.transform.offsetX = offsetX + ev.pageX - startX;
        this.transform.offsetY = offsetY + ev.pageY - startY;
      };
      document.addEventListener("mousemove", this._dragHandler);

      document.addEventListener("mouseup", ev => {
        document.removeEventListener("mousemove", this._dragHandler);
      });

      e.preventDefault();
    },
    reset() {
      this.transform = {
        scale: 1,
        deg: 0,
        offsetX: 0,
        offsetY: 0,
        enableTransition: false
      };
    },
    toggleMode() {
      if (this.loading) return;

      const modeNames = Object.keys(Mode);
      const modeValues = Object.values(Mode);
      const index = modeValues.indexOf(this.mode);
      const nextIndex = (index + 1) % modeNames.length;
      this.mode = Mode[modeNames[nextIndex]];
      this.reset();
    },
    handleActions(action, options = {}) {
      if (this.loading) return;
      const { zoomRate, rotateDeg, enableTransition } = {
        zoomRate: 0.2,
        rotateDeg: 90,
        enableTransition: true,
        ...options
      };
      const { transform } = this;
      switch (action) {
        case "zoomOut":
          if (transform.scale > 0.2) {
            transform.scale = parseFloat((transform.scale - zoomRate).toFixed(3));
          }
          break;
        case "zoomIn":
          transform.scale = parseFloat((transform.scale + zoomRate).toFixed(3));
          break;
        case "clocelise":
          transform.deg += rotateDeg;
          break;
        case "anticlocelise":
          transform.deg -= rotateDeg;
          break;
      }
      transform.enableTransition = enableTransition;
    }
  }
};
</script>

<style lang="less" scoped>
.image-viewer__wrapper {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  background: #000;

  .image-viewer__canvas {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }

  .close-btn {
    font-size: 20px;
    color: #fff;
    border: none;
    background-color: #606266;
    border-radius: 50%;
    position: absolute;
    top: 50px;
    right: 50px;
    z-index: 100;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .swiper-box {
    width: 100%;
    height: 100%;

    .img-view-item {
      width: 100%;
      height: 100%;
    }
  }
  .image-viewer__pagination {
    display: inline-block;
    width: auto;
    left: auto;
    bottom: auto;
    position: relative;
    color: #fff;
    font-size: 16px;
  }

  .image-viewer__next,
  .image-viewer__prev {
    --swiper-theme-color: #fff;
    --swiper-navigation-color: #fff;
    --swiper-navigation-size: 24px;
    background-color: #606266;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .image-viewer__next {
    right: 50px;
    padding-left: 3px;
  }

  .image-viewer__prev {
    left: 50px;
    padding-right: 3px;
  }

  .image-viewer__btn {
    position: absolute;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    opacity: 0.8;
    cursor: pointer;
    box-sizing: border-box;
    user-select: none;
    left: 50%;
    bottom: 30px;
    transform: translateX(-50%);
    width: 320px;
    height: 44px;
    padding: 0 23px;
    background-color: #606266;
    border-color: #fff;
    border-radius: 22px;
  }

  .image-viewer__actions__inner {
    width: 100%;
    height: 100%;
    text-align: justify;
    cursor: default;
    font-size: 23px;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: space-around;
  }

  .image-viewer__icon {
    cursor: pointer;
  }
}
</style>
